/**
 * `<FigureSvg>` — le rendu SVG d'une figure dans la console tuteur (G4).
 *
 * Il consomme exactement la même `FigureResolue` que `<Figure>` côté mobile,
 * produite par la même fonction pure `resoudreFigure()` de `@silex/shared`.
 * C'est la seule façon peu coûteuse de garantir que le tuteur et l'élève
 * regardent la MÊME figure : une divergence de rendu entre les deux surfaces
 * ferait discuter le tuteur d'un dessin que l'élève n'a jamais vu.
 *
 * Pourquoi un composant à part plutôt que celui du mobile : la console est une
 * application web Vite/React sans `react-native-web`. Le tracé est donc écrit
 * en SVG du DOM, mais il ne réinterprète RIEN — il traduit la même liste
 * d'affichage, avec les mêmes épaisseurs (`.ln` 2 · `.ln-2` 1,4 · `.grille` 1 ·
 * `.fort` 4,5) et les mêmes tirets (`5 5`) que la maquette.
 *
 * **Aucune couleur littérale.** Les classes de trait de la maquette (§12.1)
 * passent par les variables CSS du thème : `.ln` → `--encre`, `.ln-2` →
 * `--muted`, `.grille` → `--ligne`, `.acc` → `--acc`, `.attn` → `--attention`,
 * `.aire` → `--acc-tint`, `.lbl` → `--muted`, `.lbl-acc` → `--acc-ink`,
 * `.lbl-attn` → `--attention`. Une seule couleur écrite en dur casserait la
 * bascule entre les quatre thèmes d'accent.
 *
 * **Règle de contraste (§6.10.3).** Le TEXTE d'accent utilise `--acc-ink`,
 * jamais `--acc` seul, qui tombe sous 4,5:1 en Cyan et en Vert.
 *
 * **Accessibilité (§6.10.1).** Le SVG porte `role="img"` et un `aria-label`,
 * ET la même chaîne est rendue VISIBLE en `.fig-cap` juste dessous. Une seule
 * source — `decrireFigureResolue()` — donc jamais de divergence. Cette phrase
 * n'est pas une concession : c'est ce qui permet à un tuteur, à un parent et à
 * un lecteur d'écran de recevoir le même énoncé.
 *
 * **Aucune image n'est stockée ni transmise.** La figure est reconstruite
 * depuis sa spécification bornée, qui pèse quelques centaines d'octets, suit
 * le thème et reste nette à tout zoom.
 */
import type {
  AlignementTexte,
  CoucheResolue,
  FigureResolue,
  FigureSpecDto,
  FormeResolue,
  TonFigure,
  TraitFigure,
} from '@silex/shared';
import {
  LARGEUR_RESOLUE,
  LIGNE_BASE_TEXTE,
  TAILLE_POLICE_RESOLUE,
  decrireFigureResolue,
  resoudreFigure,
} from '@silex/shared';
import { useMemo } from 'react';

/** Les trois surfaces de figure de la maquette (§12.3). */
export type VarianteFigure = 'neutre' | 'eleve' | 'juste';

export interface FigureSvgProps {
  /** La spécification bornée. Résolue ici : une figure insoluble n'est JAMAIS affichée. */
  readonly spec: FigureSpecDto;
  /** `.fig--eleve` (filet ardoise, jamais l'Étincelle) ou `.fig--juste`. */
  readonly variante?: VarianteFigure;
  /** `.fig-tag` — une date, « Ce que l'élève a lu », « La figure de l'énoncé ». */
  readonly etiquetteSurface?: string;
  /** Légende complémentaire, AJOUTÉE sous la description — jamais à sa place. */
  readonly legendeComplement?: React.ReactNode;
}

/** `.ln` 2 · `.ln-2` 1,4 · `.grille` 1 — épaisseurs littérales de la maquette. */
const EPAISSEUR: Record<TraitFigure, number> = { principal: 2, secondaire: 1.4, grille: 1 };
/** `.fort { stroke-width: 4.5 }`. */
const EPAISSEUR_FORT = 4.5;
/** `.pointille { stroke-dasharray: 5 5 }`. */
const TIRETS = '5 5';
/** L'ancre d'un texte est une POSITION, pas une ligne de base — même ratio que le solveur. */
const DECALAGE_LIGNE_BASE = TAILLE_POLICE_RESOLUE * LIGNE_BASE_TEXTE;

const ANCRAGE_SVG: Record<AlignementTexte, 'start' | 'middle' | 'end'> = {
  debut: 'start',
  milieu: 'middle',
  fin: 'end',
};

/** Couleur d'un TRAIT. `.acc` et `.attn` écrasent la classe de base, comme en CSS. */
function couleurTrait(trait: TraitFigure, ton: TonFigure): string {
  if (ton === 'accent') return 'var(--acc)';
  if (ton === 'attention') return 'var(--attention)';
  if (trait === 'principal') return 'var(--encre)';
  if (trait === 'secondaire') return 'var(--muted)';
  return 'var(--ligne)';
}

/** Couleur d'un TEXTE. L'accent passe par `--acc-ink` — jamais `--acc` seul. */
function couleurTexte(ton: TonFigure): string {
  if (ton === 'accent') return 'var(--acc-ink)';
  if (ton === 'attention') return 'var(--attention)';
  return 'var(--muted)';
}

/** `d` d'une forme résolue. Les arcs suivent le sens trigonométrique (`sweep-flag` 0). */
export function cheminDe(forme: FormeResolue): string {
  if (forme.type === 'polyligne') {
    const [premier, ...suite] = forme.points;
    if (premier === undefined) return '';
    const corps = suite.map((p) => `L${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
    return `M${premier.x.toFixed(2)} ${premier.y.toFixed(2)} ${corps}${forme.fermee ? ' Z' : ''}`.trim();
  }
  if (forme.type === 'cercle') {
    const { centre: c, rayon: r } = forme;
    return `M${(c.x - r).toFixed(2)} ${c.y.toFixed(2)} a${r.toFixed(2)} ${r.toFixed(2)} 0 1 0 ${(2 * r).toFixed(2)} 0 a${r.toFixed(2)} ${r.toFixed(2)} 0 1 0 ${(-2 * r).toFixed(2)} 0`;
  }
  const { centre: c, rayon: r, debutDeg, finDeg } = forme;
  const pt = (a: number): string => {
    const rd = (a * Math.PI) / 180;
    return `${(c.x + r * Math.cos(rd)).toFixed(2)} ${(c.y - r * Math.sin(rd)).toFixed(2)}`;
  };
  const grandArc = Math.abs(finDeg - debutDeg) > 180 ? 1 : 0;
  const arc = `M${pt(debutDeg)} A${r.toFixed(2)} ${r.toFixed(2)} 0 ${grandArc} 0 ${pt(finDeg)}`;
  return forme.secteur
    ? `M${c.x.toFixed(2)} ${c.y.toFixed(2)} L${pt(debutDeg)} A${r.toFixed(2)} ${r.toFixed(2)} 0 ${grandArc} 0 ${pt(finDeg)} Z`
    : arc;
}

function Couche({ couche, prefixe }: { couche: CoucheResolue; prefixe: string }) {
  return (
    <>
      {couche.traces.map((trace, i) => {
        // `.aire` : un remplissage SANS trait — le seul cas où un tracé ne
        // porte pas de couleur de trait.
        const remplie = trace.remplie === true;
        return (
          <path
            key={`${prefixe}-t${i}`}
            d={cheminDe(trace.forme)}
            fill={remplie ? 'var(--acc-tint)' : 'none'}
            stroke={remplie ? undefined : couleurTrait(trace.trait, trace.ton)}
            strokeWidth={trace.style === 'fort' ? EPAISSEUR_FORT : EPAISSEUR[trace.trait]}
            strokeDasharray={trace.style === 'pointille' ? TIRETS : undefined}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
      {couche.marques.map((m, i) => (
        <circle
          key={`${prefixe}-m${i}`}
          cx={m.position.x}
          cy={m.position.y}
          r={m.rayon}
          fill={
            m.ton === 'accent'
              ? 'var(--acc)'
              : m.ton === 'attention'
                ? 'var(--attention)'
                : 'var(--encre)'
          }
        />
      ))}
      {couche.textes.map((texte, i) => (
        <text
          key={`${prefixe}-x${i}`}
          x={texte.position.x}
          y={texte.position.y + DECALAGE_LIGNE_BASE}
          fill={couleurTexte(texte.ton)}
          fontFamily="var(--f-data)"
          fontSize={TAILLE_POLICE_RESOLUE}
          fontWeight={texte.ton === 'neutre' ? 400 : 600}
          textAnchor={ANCRAGE_SVG[texte.alignement]}
        >
          {texte.texte}
        </text>
      ))}
    </>
  );
}

/**
 * Rend une figure résolue. Retourne `null` si la spécification est insoluble :
 * jamais une figure approximative présentée comme fiable (§3.4, FR-4.9) —
 * l'écran appelant affiche alors ce qu'il a, sans figure.
 */
export function FigureSvg({
  spec,
  variante = 'neutre',
  etiquetteSurface,
  legendeComplement,
}: FigureSvgProps) {
  const resolue: FigureResolue | null = useMemo(() => resoudreFigure(spec), [spec]);
  const description = useMemo(
    () => (resolue === null ? '' : decrireFigureResolue(spec, resolue)),
    [spec, resolue],
  );

  if (resolue === null) return null;

  const classe =
    variante === 'eleve' ? 'fig fig--eleve' : variante === 'juste' ? 'fig fig--juste' : 'fig';

  return (
    <div className={classe}>
      {etiquetteSurface ? <span className="fig-tag">{etiquetteSurface}</span> : null}
      <svg
        viewBox={`0 0 ${LARGEUR_RESOLUE} ${resolue.boite.hauteur}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={description}
      >
        <Couche couche={resolue.figure} prefixe="fig" />
        {/* La couche d'ANNOTATIONS est posée PAR-DESSUS la géométrie : c'est
            une affirmation de l'élève, jamais une propriété de la figure. */}
        <Couche couche={resolue.annotations} prefixe="ann" />
      </svg>
      <span className="fig-cap">
        {description}
        {/* Le complément est une SECONDE LIGNE de légende, jamais la suite de
            la phrase de description : mélangés, les deux se lisaient comme un
            paragraphe unique où l'on ne savait plus ce qui décrit la figure et
            ce qui rapporte la lecture de l'élève. */}
        {legendeComplement ? <span className="fig-cap__ligne">{legendeComplement}</span> : null}
      </span>
    </div>
  );
}
